import { useEffect, useState } from 'react';

import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { useGetInstitutionCoachesQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';

import { CoachBadge } from './CoachBadge';

const {
  modules: { coachingDashboard },
} = appStrings;

export type CoachAssignmentModalViewProps = Readonly<{
  open: boolean;
  onClose: () => void;
  description: string;
  initialSelectedCoaches: CoachSummary[];
  lockedCoachIds?: Set<number>;
  requireSelection?: boolean;
  isSaving: boolean;
  errorMessage: string | null;
  onCloseError: () => void;
  successMessage?: string | null;
  onCloseSuccess?: () => void;
  onSave: (selected: CoachSummary[]) => void | Promise<void>;
}>;

export const CoachAssignmentModalView = ({
  open,
  onClose,
  description,
  initialSelectedCoaches,
  lockedCoachIds,
  requireSelection = false,
  isSaving,
  errorMessage,
  onCloseError,
  successMessage,
  onCloseSuccess,
  onSave,
}: CoachAssignmentModalViewProps) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 300);
  const [selectedCoaches, setSelectedCoaches] = useState<CoachSummary[]>([]);

  const { data: institutionCoaches, isFetching: isFetchingCoaches } = useGetInstitutionCoachesQuery(
    { q: debouncedInput },
    { skip: !open },
  );

  useEffect(() => {
    if (open) {
      setSelectedCoaches(initialSelectedCoaches);
      setInputValue('');
    }
  }, [open, initialSelectedCoaches]);

  const locked = lockedCoachIds ?? new Set<number>();

  const handleRemoveCoach = (coachId: number) => {
    if (locked.has(coachId)) return;

    setSelectedCoaches((prev) => prev.filter((c) => c.id !== coachId));
  };

  const handleSave = () => onSave(selectedCoaches);

  const saveDisabled = isSaving || (requireSelection && selectedCoaches.length === 0);

  return (
    <>
      <InfoModal
        open={open}
        onClose={onClose}
        width="sm"
        featuredIcon={{
          iconName: 'Users01',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={
          <Typography variant="h6">{coachingDashboard.seatActions.assignCoaches}</Typography>
        }
        bodyContent={
          <Box sx={modalBodySx}>
            <Typography variant="text-sm-regular" sx={modalDescriptionSx}>
              {description}
            </Typography>
            <Autocomplete
              id="coach-assignment-autocomplete"
              multiple
              disableCloseOnSelect
              options={institutionCoaches ?? []}
              value={selectedCoaches}
              onChange={(_, next) => setSelectedCoaches(next)}
              inputValue={inputValue}
              onInputChange={(_, v) => setInputValue(v)}
              loading={isFetchingCoaches}
              filterOptions={(x) => x}
              getOptionLabel={(c) => personName(c)}
              getOptionDisabled={(option) => locked.has(option.id)}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={
                debouncedInput.trim().length > 0
                  ? coachingDashboard.bulkAssignNoSearchResults
                  : coachingDashboard.noCoachesAvailable
              }
              renderTags={() => null}
              renderOption={(props, coach) => (
                <Box component="li" {...props} key={coach.id} sx={optionRowSx}>
                  <Avatar
                    src={coach.profile_image_url ?? undefined}
                    alt={personName(coach)}
                    sx={optionAvatarSx}
                  >
                    {personInitial(coach)}
                  </Avatar>
                  <Typography variant="text-sm-medium">{personName(coach)}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={coachingDashboard.bulkAssignSearchPlaceholder}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AppIcon
                          iconName="SearchMd"
                          diameter="20px"
                          color="Colors/Foreground/fg-quaternary"
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {isFetchingCoaches ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedCoaches.length > 0 && (
              <Box sx={selectedChipsSx}>
                {selectedCoaches.map((coach) => {
                  const isLocked = locked.has(coach.id);

                  return (
                    <CoachBadge
                      key={coach.id}
                      id={`bulk-assign-selected-coach-${coach.id}`}
                      firstName={coach.first_name}
                      lastName={coach.last_name}
                      profileImageUrl={coach.profile_image_url ?? null}
                      onDelete={isLocked ? undefined : () => handleRemoveCoach(coach.id)}
                      disabled={isSaving}
                    />
                  );
                })}
              </Box>
            )}
            <Box sx={modalActionsSx}>
              <Button
                id="coaching-dashboard-bulk-assign-cancel-button"
                hierarchy="Secondary gray"
                size="md"
                onClick={onClose}
                disabled={isSaving}
              >
                {coachingDashboard.coachAssignmentCancel}
              </Button>
              <Button
                id="coaching-dashboard-bulk-assign-confirm-button"
                hierarchy="Primary"
                size="md"
                onClick={handleSave}
                disabled={saveDisabled}
              >
                {isSaving
                  ? coachingDashboard.coachAssignmentSaving
                  : coachingDashboard.coachAssignmentSave}
              </Button>
            </Box>
          </Box>
        }
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={onCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={onCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={onCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={onCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const modalBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const modalDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const optionRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const optionAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: 32,
  height: 32,
  fontSize: '0.875rem',
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});

const selectedChipsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const modalActionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  justifyContent: 'flex-end',
  paddingTop: backrs.spacing['spacing-md'],
});
